import { useRef } from "react";
import { createPortal } from "react-dom";
import jsPDF from "jspdf";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useIsMobile } from "@/components/ResponsiveModal";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Heart, Download, Flame, RefreshCw, ChefHat, CookingPot, ThumbsDown, Share2 } from "lucide-react";

const APP_URL = "https://app.tuha.app";

function MacroPill({ label, value, unit }) {
  return (
    <div className="bg-surface rounded-xl px-3 py-2 border border-border text-center min-w-[64px]">
      <div className="font-serif-display text-xl text-primary leading-none">{Math.round(value || 0)}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{label}{unit ? ` (${unit})` : ""}</div>
    </div>
  );
}

export default function RecipeCardDialog({ open, recipe, loading, liked, onToggleLike, onClose, onCook, cooking, onDislike, disliking }) {
  const cardRef = useRef(null);
  const isMobile = useIsMobile();

  const downloadPdf = async () => {
    if (!recipe) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 40;
    let y = M;
    const line = (txt, size, style = "normal", color = [40, 40, 40], gap = 16) => {
      doc.setFont("helvetica", style); doc.setFontSize(size); doc.setTextColor(...color);
      const wrapped = doc.splitTextToSize(txt, W - M * 2);
      wrapped.forEach((w) => {
        if (y > H - M) { doc.addPage(); y = M; }
        doc.text(w, M, y); y += gap;
      });
    };
    // Header
    doc.setFillColor(46, 64, 56); doc.rect(0, 0, W, 8, "F");
    line(recipe.dish || "Recipe", 22, "bold", [46, 64, 56], 26);
    line(`Designed for ${recipe.for_name || "you"}  ·  ${(recipe.meal || "").toUpperCase()}`, 11, "normal", [110, 110, 110], 16);
    const meta = [];
    if (recipe.servings) meta.push(`Serves ${recipe.servings}`);
    if (recipe.prep_min) meta.push(`Prep ${recipe.prep_min} min`);
    if (recipe.cook_min) meta.push(`Cook ${recipe.cook_min} min`);
    if (meta.length) line(meta.join("   •   "), 10, "italic", [130, 130, 130], 18);
    y += 6;

    // Per-meal macros
    const t = recipe.totals || {};
    line("PER-MEAL MACROS (this meal only)", 11, "bold", [46, 64, 56], 16);
    line(`Calories ${Math.round(t.calories || 0)} kcal   |   Protein ${Math.round(t.protein_g || 0)} g   |   Carbs ${Math.round(t.carbs_g || 0)} g   |   Fat ${Math.round(t.fat_g || 0)} g   |   Fiber ${Math.round(t.fiber_g || 0)} g`, 10, "normal", [60, 60, 60], 18);
    y += 4;

    // Ingredients table
    line("RAW INGREDIENTS & MACROS", 11, "bold", [46, 64, 56], 16);
    doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(120, 120, 120);
    const cols = [M, M + 170, M + 250, M + 310, M + 365, M + 420, M + 475];
    const heads = ["Ingredient", "Qty", "kcal", "P", "C", "F", "Fiber"];
    if (y > H - M) { doc.addPage(); y = M; }
    heads.forEach((h, i) => doc.text(h, cols[i], y));
    y += 4; doc.setDrawColor(220, 220, 220); doc.line(M, y, W - M, y); y += 12;
    doc.setFont("helvetica", "normal"); doc.setTextColor(50, 50, 50);
    (recipe.ingredients || []).forEach((ing) => {
      if (y > H - M) { doc.addPage(); y = M; }
      const name = doc.splitTextToSize(ing.name || "", 160);
      doc.text(name[0] || "", cols[0], y);
      doc.text(`${ing.quantity || 0} ${ing.unit || ""}`, cols[1], y);
      doc.text(`${Math.round(ing.calories || 0)}`, cols[2], y);
      doc.text(`${Math.round(ing.protein_g || 0)}`, cols[3], y);
      doc.text(`${Math.round(ing.carbs_g || 0)}`, cols[4], y);
      doc.text(`${Math.round(ing.fat_g || 0)}`, cols[5], y);
      doc.text(`${Math.round(ing.fiber_g || 0)}`, cols[6], y);
      y += 15;
    });
    y += 8;

    // Cooking method
    line("COOKING METHOD", 11, "bold", [46, 64, 56], 16);
    (recipe.steps || []).forEach((s, i) => line(`${i + 1}. ${s}`, 10, "normal", [50, 50, 50], 16));
    if (recipe.tip) { y += 4; line(`KAAYA tip: ${recipe.tip}`, 10, "italic", [120, 120, 120], 16); }

    // Footer
    doc.setFontSize(9); doc.setTextColor(160, 160, 160);
    doc.text("Generated by KAAYA", M, H - 20);

    const filename = `${(recipe.dish || "recipe").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;

    // NATIVE APP (Capacitor): browser downloads don't work in the WebView, so write the
    // PDF to device storage and hand it to the native share/save sheet.
    if (Capacitor.isNativePlatform()) {
      try {
        const dataUri = doc.output("datauristring"); // data:application/pdf;base64,....
        const base64 = dataUri.substring(dataUri.indexOf(",") + 1);
        const written = await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: recipe.dish || "KAAYA recipe",
          text: `${recipe.dish || "Recipe"} — from KAAYA`,
          url: written.uri,
          dialogTitle: "Save or share your recipe",
        });
      } catch (e) {
        if (!(e && (e.message || "").toLowerCase().includes("cancel"))) {
          toast.error("Couldn't save the recipe PDF — please try again.");
        }
      }
      return;
    }

    // WEB: try Web Share with the file (mobile browsers), else a normal download (desktop).
    try {
      const blob = doc.output("blob");
      const file = new File([blob], filename, { type: "application/pdf" });
      if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: recipe.dish || "KAAYA recipe" });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // user dismissed the share sheet
    }
    doc.save(filename);
  };

  const overlay = disliking ? createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center text-center px-8 bg-background/92 backdrop-blur-sm"
      data-testid="recipe-regenerating-overlay"
    >
      <RefreshCw size={28} className="mb-4 animate-spin text-secondary" />
      <div className="font-serif-display text-2xl sm:text-3xl text-primary">Finding a recipe you'll love better…</div>
      <div className="text-sm text-muted-foreground mt-2 max-w-sm">Cooking up a fresh option tailored to you. This takes a few seconds — please don't close or refresh this page.</div>
    </div>,
    document.body
  ) : null;

  const loadingBlock = (
    <div className="py-16 text-center text-muted-foreground" data-testid="recipe-loading">
      <RefreshCw size={22} className="mx-auto mb-3 animate-spin text-secondary" />
      Plating up your recipe…
    </div>
  );

  const recipeBody = recipe ? (
    <div ref={cardRef}>
      {/* Designed for */}
      <div className="text-overline text-secondary flex items-center gap-1"><ChefHat size={13} /> recipe card</div>
      <h2 className="font-serif-display text-3xl text-primary leading-tight mt-1" data-testid="recipe-dish">{recipe.dish}</h2>
      <div className="text-sm text-muted-foreground mt-1" data-testid="recipe-for">
        Designed for <span className="text-foreground/80 font-medium">{recipe.for_name || "you"}</span> · <span className="capitalize">{recipe.meal}</span>
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-2">
        {recipe.servings ? <span>Serves {recipe.servings}</span> : null}
        {recipe.prep_min ? <span>· Prep {recipe.prep_min} min</span> : null}
        {recipe.cook_min ? <span>· Cook {recipe.cook_min} min</span> : null}
      </div>
      {recipe.why && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-secondary/10 border border-secondary/20 px-3 py-2" data-testid="recipe-why">
          <ChefHat size={14} className="text-secondary mt-0.5 shrink-0" />
          <span className="text-xs text-foreground/80">{recipe.why}</span>
        </div>
      )}

      {/* Per-meal macros */}
      <div className="mt-4">
        <div className="text-overline mb-2 flex items-center gap-1"><Flame size={12} /> per-meal macros</div>
        <div className="flex flex-wrap gap-2" data-testid="recipe-macros">
          <MacroPill label="kcal" value={recipe.totals?.calories} />
          <MacroPill label="protein" value={recipe.totals?.protein_g} unit="g" />
          <MacroPill label="carbs" value={recipe.totals?.carbs_g} unit="g" />
          <MacroPill label="fat" value={recipe.totals?.fat_g} unit="g" />
          <MacroPill label="fiber" value={recipe.totals?.fiber_g} unit="g" />
        </div>
      </div>

      {/* Ingredients */}
      <div className="mt-5">
        <div className="text-overline mb-2">raw ingredients & macros</div>
        <div className="overflow-hidden rounded-2xl border border-border" data-testid="recipe-ingredients">
          <table className="w-full text-sm">
            <thead className="bg-surface text-muted-foreground">
              <tr>
                <th className="text-left text-overline px-3 py-2">Ingredient</th>
                <th className="text-right text-overline px-2 py-2">Qty</th>
                <th className="text-right text-overline px-2 py-2">kcal</th>
                <th className="text-right text-overline px-2 py-2">P</th>
                <th className="text-right text-overline px-2 py-2">C</th>
                <th className="text-right text-overline px-2 py-2">F</th>
                <th className="text-right text-overline px-3 py-2">Fib</th>
              </tr>
            </thead>
            <tbody>
              {(recipe.ingredients || []).map((ing, i) => (
                <tr key={`${ing.name || "ing"}-${i}`} className="border-t border-border">
                  <td className="px-3 py-2 text-foreground/90">{ing.name}</td>
                  <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{ing.quantity} {ing.unit}</td>
                  <td className="px-2 py-2 text-right">{Math.round(ing.calories || 0)}</td>
                  <td className="px-2 py-2 text-right">{Math.round(ing.protein_g || 0)}</td>
                  <td className="px-2 py-2 text-right">{Math.round(ing.carbs_g || 0)}</td>
                  <td className="px-2 py-2 text-right">{Math.round(ing.fat_g || 0)}</td>
                  <td className="px-3 py-2 text-right">{Math.round(ing.fiber_g || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cooking method */}
      <div className="mt-5">
        <div className="text-overline mb-2">cooking method</div>
        <ol className="space-y-2" data-testid="recipe-steps">
          {(recipe.steps || []).map((s, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span className="shrink-0 h-6 w-6 rounded-full bg-secondary/15 text-secondary flex items-center justify-center text-xs font-medium">{i + 1}</span>
              <span className="text-foreground/90 pt-0.5">{s}</span>
            </li>
          ))}
        </ol>
        {recipe.tip && <p className="text-sm text-muted-foreground italic mt-3">KAAYA tip: {recipe.tip}</p>}
      </div>
    </div>
  ) : null;

  const shareRecipe = async () => {
    if (!recipe) return;
    const t = recipe.totals || {};
    const macros = `${Math.round(t.calories || 0)} kcal · P${Math.round(t.protein_g || 0)} C${Math.round(t.carbs_g || 0)} F${Math.round(t.fat_g || 0)} (this meal)`;
    const text =
      `🍽️ ${recipe.dish}\n\n` +
      `A personalized recipe made for me by KAAYA — the ultimate AI health & nutrition app. 🥗\n\n` +
      `${macros}\n\n` +
      `Get your own free personalized recipes & meal plans 👉 ${APP_URL}\n` +
      `Sign up free and try it today! #KAAYAOS`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${recipe.dish} · KAAYA`, text, url: APP_URL });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Recipe copied — paste it anywhere to share!");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        try { await navigator.clipboard.writeText(text); toast.success("Recipe copied — paste it anywhere to share!"); }
        catch (_) { toast.error("Couldn't share — please try again."); }
      }
    }
  };

  const actions = recipe ? (
    <div className="flex flex-wrap items-center justify-end gap-2" data-testid="recipe-actions">
      <Button
        variant="outline"
        onClick={onToggleLike}
        className={`rounded-full ${liked ? "border-green-400 text-green-700 bg-green-50" : ""}`}
        data-testid="recipe-like-btn"
      >
        <Heart size={15} className={`mr-1 ${liked ? "fill-green-600 text-green-600" : ""}`} /> {liked ? "Liked" : "Like"}
      </Button>
      {onDislike && (
        <Button
          variant="outline"
          onClick={() => onDislike(recipe)}
          disabled={disliking}
          className="rounded-full hover:border-destructive hover:text-destructive"
          data-testid="recipe-dislike-btn"
        >
          <ThumbsDown size={15} className="mr-1" /> {disliking ? "Finding another…" : "Dislike"}
        </Button>
      )}
      <Button onClick={shareRecipe} variant="outline" className="rounded-full" data-testid="recipe-share-btn">
        <Share2 size={15} className="mr-1" /> Share
      </Button>
      <Button onClick={downloadPdf} variant="outline" className="rounded-full" data-testid="recipe-download-btn">
        <Download size={15} className="mr-1" /> Download PDF
      </Button>
      {onCook && (
        <Button onClick={() => onCook(recipe)} disabled={cooking} className="rounded-full bg-primary hover:bg-primary/90" data-testid="recipe-cook-btn">
          <CookingPot size={15} className="mr-1" /> {cooking ? "Logging…" : "Log Meal"}
        </Button>
      )}
    </div>
  ) : null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => { if (!o && !disliking) onClose?.(); }} dismissible={!disliking}>
        <DrawerContent className="bg-surface-elevated max-h-[94dvh] z-[70]" data-testid="recipe-card-dialog">
          {overlay}
          <DrawerTitle className="sr-only">{recipe?.dish || "Recipe card"}</DrawerTitle>
          <div className="flex flex-col min-h-0 max-h-[92dvh]">
            <div className="px-4 pt-2 flex-1 overflow-y-auto scrollbar-thin pb-3">
              {loading || !recipe ? loadingBlock : recipeBody}
            </div>
            {!loading && recipe && (
              <div className="px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-border bg-surface-elevated">
                {actions}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !disliking) onClose?.(); }}>
      <DialogContent
        className="rounded-3xl max-w-2xl max-h-[88vh] overflow-y-auto scrollbar-thin z-[70]"
        data-testid="recipe-card-dialog"
        onInteractOutside={(e) => { if (disliking) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (disliking) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (disliking) e.preventDefault(); }}
      >
        {overlay}
        <DialogTitle className="sr-only">{recipe?.dish || "Recipe card"}</DialogTitle>
        <DialogDescription className="sr-only">Personalized recipe card with per-meal macros and cooking method.</DialogDescription>
        {loading || !recipe ? loadingBlock : (
          <div>
            {recipeBody}
            <div className="mt-6 pt-4 border-t border-border">{actions}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
